import React, {useState, useEffect, useMemo} from 'react';
import {Drawer, Button, Checkbox, Input, Tag, Image, message, Skeleton, Tooltip} from 'antd';
import {CheckOutlined, CloseOutlined, ArrowLeftOutlined, CameraOutlined, QuestionCircleOutlined} from '@ant-design/icons';
import {t} from 'i18n';
import {PodReviewDetailType} from '../graphql/types/PodReviewType';
import {usePodReviewActions} from '../hooks/usePodReviewActions';
import CentralParcelDetailDrawer from '../../../central/parcels/components/CentralParcelDetailDrawer';
import {getUserInfo} from '../../../../utils/auth';
import {useLazyQuery} from '@apollo/client';
import {GET_POD_REVIEW_DETAIL_BY_ID, GetPodReviewDetailByIdResponse} from '../graphql/queries/getPodReviewDetailById';
import {calculateDistance} from '../../../../utils/distanceUtils';
import {getErrorKeys, showError} from '../../../../utils/exceptionUtils';
import {usePodExemptDict} from '../../../../hooks/usePodExemptDict';

// 常量定义
const POD_REVIEW_STATUS = {
    MANUAL_APPROVED: 4,
    MANUAL_DISAPPROVED: 5,
    MANUAL_CORRECTED: 6,
} as const;

const L1_L2_REASONS = [
    {l1Code: 'LABEL_PHOTO', l2Codes: ['UNRECOGNIZABLE_LABEL', 'MISSING_LABEL']},
    {l1Code: 'ADDRESS_PHOTO', l2Codes: ['WRONG_ADDRESS', 'MISSING_ADDRESS_PHOTO', 'INCOMPLETE_ADDRESS_INFO']},
    {l1Code: 'DELIVERY_LOCATION_PHOTO', l2Codes: ['DELIVERED_TO_USPS_BOX', 'DELIVERED_TO_POST_OFFICE', 'DELIVERED_TO_OUTDOOR_UNSAFE', 'DELIVERED_TO_INDOOR_UNSAFE', 'MISSING_DELIVERY_LOCATION_PHOTO']},
    {l1Code: 'SPECIAL_VIOLATION', l2Codes: ['FALSE_DELIVERY', 'USER_PRIVACY', 'DRIVER_FRAUD']},
];

// 三级原因：仅「送错地址」下挂三级，其余二级均为叶子节点
const L3_BY_L2: Record<string, string[]> = {
    WRONG_ADDRESS: ['WRONG_STREET_NUMBER', 'WRONG_HOUSE_NUMBER', 'WRONG_UNIT_NUMBER'],
};

const L2_TO_L1_MAP: Record<string, string> = L1_L2_REASONS.reduce((acc, {l1Code, l2Codes}) => {
    l2Codes.forEach(l2 => { acc[l2] = l1Code; });
    return acc;
}, {} as Record<string, string>);

// 三级 code → 其父二级 code
const L3_TO_L2_MAP: Record<string, string> = Object.entries(L3_BY_L2).reduce((acc, [l2, l3s]) => {
    l3s.forEach(l3 => { acc[l3] = l2; });
    return acc;
}, {} as Record<string, string>);

const LEGACY_TO_L2_MAP: Record<string, {l1Code: string; l2Code: string}> = {
    NO_LABEL_PHOTO: {l1Code: 'LABEL_PHOTO', l2Code: 'MISSING_LABEL'},
    WRONG_TRK: {l1Code: 'ADDRESS_PHOTO', l2Code: 'WRONG_ADDRESS'},
    NO_HOUSE_APT: {l1Code: 'ADDRESS_PHOTO', l2Code: 'INCOMPLETE_ADDRESS_INFO'},
    WRONG_HOUSE_APT: {l1Code: 'ADDRESS_PHOTO', l2Code: 'WRONG_ADDRESS'},
};

// 选择模型：{ l1Code: { l2Code: [l3Code...] } }
// key 存在即表示该层被勾选；三级数组为空表示只勾到二级
type ReasonSelection = Record<string, Record<string, string[]>>;

// 工具函数
// 返回最终提交的 codes：始终只提交最细一级（有 L3 提 L3，否则 L2，都没有则 L1）
const getAllSelectedCodes = (sel: ReasonSelection): string[] => {
    const result: string[] = [];
    for (const [l1Code, l2Map] of Object.entries(sel)) {
        const l2Codes = Object.keys(l2Map);
        if (l2Codes.length === 0) {
            result.push(l1Code);
            continue;
        }
        for (const l2Code of l2Codes) {
            const l3Codes = l2Map[l2Code] ?? [];
            if (l3Codes.length > 0) {
                result.push(...l3Codes);
            } else {
                result.push(l2Code);
            }
        }
    }
    return result;
};

const extractLegacyOtherText = (messageList: string[] | undefined): string => {
    if (!messageList || messageList.length === 0) return '';
    return messageList
        .filter(code => {
            if (!code || code === 'MANUAL_APPROVED' || code === 'OTHER') return false;
            if (L1_L2_REASONS.some(({l1Code}) => l1Code === code)) return false;
            if (L2_TO_L1_MAP[code]) return false;
            if (L3_TO_L2_MAP[code]) return false;
            if (LEGACY_TO_L2_MAP[code]) return false;
            return true;
        })
        .join('; ');
};

const hasSubstantialReasonChange = (
    current: ReasonSelection,
    original: ReasonSelection
) => {
    const toSorted = (m: ReasonSelection) =>
        getAllSelectedCodes(m).sort().join(',');
    return toSorted(current) !== toSorted(original);
};

// 已选原因的完整路径文案，如「地址信息照片不合格 / 送错地址 / 送错门牌号」
interface ReasonChip {
    key: string;
    text: string;
    l1Code: string;
    l2Code?: string;
    l3Code?: string;
}

const buildReasonChips = (sel: ReasonSelection): ReasonChip[] => {
    const chips: ReasonChip[] = [];
    const label = (code: string) => t(`podReview.reasons.${code}`, {defaultValue: code});
    for (const {l1Code, l2Codes} of L1_L2_REASONS) {
        const l2Map = sel[l1Code];
        if (!l2Map) continue;
        const chosenL2 = l2Codes.filter(l2 => l2 in l2Map);
        if (chosenL2.length === 0) {
            chips.push({key: l1Code, text: label(l1Code), l1Code});
            continue;
        }
        for (const l2Code of chosenL2) {
            const l3Codes = l2Map[l2Code] ?? [];
            if (l3Codes.length > 0) {
                for (const l3Code of l3Codes) {
                    chips.push({
                        key: `${l1Code}/${l2Code}/${l3Code}`,
                        text: `${label(l1Code)} / ${label(l2Code)} / ${label(l3Code)}`,
                        l1Code, l2Code, l3Code,
                    });
                }
            } else {
                chips.push({
                    key: `${l1Code}/${l2Code}`,
                    text: `${label(l1Code)} / ${label(l2Code)}`,
                    l1Code, l2Code,
                });
            }
        }
    }
    return chips;
};

const formatReviewMessage = (messageList: string[] | undefined): string => {
    if (!messageList || messageList.length === 0) return '';
    return messageList
        .filter(code => code && code !== 'OTHER')
        .map(code => t(`podReview.reasons.${code}`, {defaultValue: code}))
        .join('; ');
};

// 结论按钮样式（沿用旧版交互）：默认中性灰，仅悬浮时透出绿/红，选中态才实心高亮
const verdictButtonClass = (isApprove: boolean, selected: boolean) => {
    const base = 'font-semibold';
    if (selected) {
        return isApprove
            ? `${base} !text-green-700 !bg-green-50 !border-green-500 hover:!bg-green-100`
            : `${base} !text-red-700 !bg-red-50 !border-red-500 hover:!bg-red-100`;
    }
    return isApprove
        ? `${base} !text-gray-600 !bg-white !border-gray-300 hover:!text-green-700 hover:!bg-green-50 hover:!border-green-500`
        : `${base} !text-gray-600 !bg-white !border-gray-300 hover:!text-red-700 hover:!bg-red-50 hover:!border-red-500`;
};

interface PodReviewDetailDrawerProps {
    visible: boolean;
    onClose: () => void;
    reviewIds: string[];
    isEditMode?: boolean;
}

const PodReviewDetailDrawer: React.FC<PodReviewDetailDrawerProps> = ({
                                                                         visible,
                                                                         onClose,
                                                                         reviewIds = [],
                                                                         isEditMode = false,
                                                                     }) => {
    const [reasonSelection, setReasonSelection] = useState<ReasonSelection>({});
    // 三栏联动：当前聚焦的一级 / 二级（仅控制右侧栏展示哪些子项，与勾选状态相互独立）
    const [focusedL1, setFocusedL1] = useState<string>(L1_L2_REASONS[0].l1Code);
    const [focusedL2, setFocusedL2] = useState<string | null>(null);
    // 不合格原因弹窗（点「不批准」后弹出）
    const [reasonModalVisible, setReasonModalVisible] = useState(false);
    // 非编辑模式下，对已有结论点「修改审核结果」后放开顶栏按钮
    const [editingSavedReview, setEditingSavedReview] = useState(false);
    const [legacyOtherText, setLegacyOtherText] = useState<string>('');
    const [reviewNote, setReviewNote] = useState<string>('');
    const [showNoMore, setShowNoMore] = useState(false);
    const [currentOrder, setCurrentOrder] = useState<PodReviewDetailType | null>(null);
    // 豁免原因 code→名称：字典项由全局 Apollo 缓存共享（cache-and-network 先读缓存不阻塞渲染）
    const {items: podExemptReasonDict} = usePodExemptDict('REASON');
    const podExemptReasonName = useMemo(() => {
        const code = currentOrder?.podExemptReasonCode;
        if (!code) {
            return null;
        }
        return podExemptReasonDict.find((item) => item.code === code)?.name ?? code;
    }, [currentOrder?.podExemptReasonCode, podExemptReasonDict]);
    // 多条数据时，内部维护当前索引（用于显示标题和获取当前id）
    const [internalCurrentIndex, setInternalCurrentIndex] = useState<number>(0);

    // 保存原始状态，用于取消编辑时恢复
    const [originalReasonSelection, setOriginalReasonSelection] = useState<ReasonSelection>({});

    // 编辑模式下的临时选择状态
    const [tempSelectedStatus, setTempSelectedStatus] = useState<boolean | null>(null); // true=approve, false=disapprove, null=未选择
    // 编辑模式下，审核员是否已主动点击过批准/不批准按钮（点击前表单禁用）
    const {handleReview, loading} = usePodReviewActions();

    const [getPodReviewDetail, {loading: detailLoading}] = useLazyQuery<GetPodReviewDetailByIdResponse>(
        GET_POD_REVIEW_DETAIL_BY_ID,
        {fetchPolicy: 'network-only', nextFetchPolicy: 'network-only'}
    );
    const [isProcessing, setIsProcessing] = useState(false);
    const [lastErrorMessage, setLastErrorMessage] = useState<string>('');
    const [lastErrorTime, setLastErrorTime] = useState<number>(0);
    const [parcelDetailVisible, setParcelDetailVisible] = useState(false);

    const podImages = currentOrder?.podImages ?? [];

    // 计算距离（前端计算，使用与后端一致的Haversine公式）
    const deliveryDistance = useMemo(() => {
        const deliveredLoc = currentOrder?.deliveredLocation;
        const recipientLoc = currentOrder?.recipientAddress;
        
        if (!deliveredLoc || !recipientLoc?.latitude || !recipientLoc?.longitude) {
            return null;
        }
        
        return calculateDistance(
            recipientLoc.latitude,
            recipientLoc.longitude,
            deliveredLoc.latitude,
            deliveredLoc.longitude
        );
    }, [currentOrder?.deliveredLocation, currentOrder?.recipientAddress]);

    const showErrorOnce = (errorMessage: string) => {
        const now = Date.now();
        if (lastErrorMessage === errorMessage && now - lastErrorTime < 3000) {
            return;
        }
        setLastErrorMessage(errorMessage);
        setLastErrorTime(now);
        message.error(errorMessage);
    };

    const prevQueryIdRef = React.useRef<string | undefined>(undefined);
    const prevVisibleRef = React.useRef<boolean>(false);
    const prevReviewIdsRef = React.useRef<string[]>([]);

    const isBatchMode = reviewIds.length > 1;
    const queryId = reviewIds[internalCurrentIndex];

    useEffect(() => {
        const idsChanged =
            prevReviewIdsRef.current.length !== reviewIds.length ||
            !prevReviewIdsRef.current.every((id, index) => id === reviewIds[index]);

        if (idsChanged) {
            setInternalCurrentIndex(0);
            prevReviewIdsRef.current = reviewIds;
        }
    }, [reviewIds]);

    const fetchDetailById = (id: string, shouldClearCurrentOrder: boolean = true) => {
        const resetState = () => {
            setReasonSelection({});
            setOriginalReasonSelection({});
            setLegacyOtherText('');
            setReviewNote('');
            setShowNoMore(false);
            setIsProcessing(false);
            setLastErrorMessage('');
            setLastErrorTime(0);
            setTempSelectedStatus(null);
            setEditingSavedReview(false);
            setReasonModalVisible(false);
        };

        if (shouldClearCurrentOrder) {
            setCurrentOrder(null);
        }
        resetState();

        return getPodReviewDetail({
            variables: {podManualReviewId: id},
            fetchPolicy: 'network-only' // 强制从网络获取最新数据，避免使用缓存
        }).then((result) => {
            // useLazyQuery 遇到 GraphQL errors 时 promise 不会 reject，error 挂在 result 上
            if (result.error) {
                // 后端 order 存在性校验失败会抛 errors.business.order.notExist，统一提示「订单不存在或已归档」
                if (getErrorKeys(result.error).messageKey === 'errors.business.order.notExist') {
                    showError(result.error);
                    return;
                }
                message.error(t('podReview.messages.fetchDetailError') || result.error.message);
                return;
            }
            if (result.data?.getPodReviewDetailById) {
                if (!shouldClearCurrentOrder) {
                    resetState();
                }
                setCurrentOrder(result.data.getPodReviewDetailById);
            } else {
                message.error(t('podReview.messages.detailNotFound'));
            }
        }).catch((error) => {
            if (getErrorKeys(error).messageKey === 'errors.business.order.notExist') {
                showError(error);
                return;
            }
            message.error(t('podReview.messages.fetchDetailError') || error.message);
        });
    };

    useEffect(() => {
        // 关闭时必须先重置 ref，否则下次打开同一条 queryId 相等会被误判为"已查询"，导致不刷新
        if (!visible) {
            prevQueryIdRef.current = undefined;
            prevVisibleRef.current = false;
            if (!isBatchMode) {
                setInternalCurrentIndex(0);
            }
            return;
        }

        if (!queryId) {
            return;
        }

        const queryIdChanged = prevQueryIdRef.current !== queryId;
        const visibleChanged = !prevVisibleRef.current;

        if (queryIdChanged || visibleChanged) {
            const shouldClearCurrentOrder = !(isBatchMode && queryIdChanged && prevQueryIdRef.current !== undefined);
            fetchDetailById(queryId, shouldClearCurrentOrder);
            prevQueryIdRef.current = queryId;
        }

        prevVisibleRef.current = visible;
    }, [getPodReviewDetail, queryId, visible, isBatchMode]);

    useEffect(() => {
        if (currentOrder) {
            const isApproved = currentOrder.podReviewStatus === POD_REVIEW_STATUS.MANUAL_APPROVED;

            if (isApproved) {
                setReasonSelection({});
                setOriginalReasonSelection({});
                setLegacyOtherText('');
            } else {
                const parsed = parseReviewReasons(currentOrder.podReviewMessage);
                setReasonSelection(parsed);
                setOriginalReasonSelection(parsed);
                setLegacyOtherText(extractLegacyOtherText(currentOrder.podReviewMessage));
            }
            setReviewNote(currentOrder.podReviewNote ?? '');

            if (isEditMode) {
                setTempSelectedStatus(
                    currentOrder.podReviewStatus === POD_REVIEW_STATUS.MANUAL_APPROVED ? true :
                        currentOrder.podReviewStatus === POD_REVIEW_STATUS.MANUAL_DISAPPROVED ? false :
                            currentOrder.podReviewStatus === POD_REVIEW_STATUS.MANUAL_CORRECTED ? true : null
                );
            }

            if (isBatchMode) {
                const current = internalCurrentIndex + 1;
                const isLastItem = current === reviewIds.length;
                setShowNoMore(isLastItem);
            }

            setIsProcessing(false);
            setLastErrorMessage('');
            setLastErrorTime(0);
        }
    }, [currentOrder?.id, currentOrder?.podReviewMessage, currentOrder?.podReviewStatus, isEditMode, isBatchMode, reviewIds.length]);

    useEffect(() => {
        if (!visible) {
            setShowNoMore(false);
            setEditingSavedReview(false);
            setReasonModalVisible(false);
        }
    }, [visible]);

    // 解析审核消息中的原因，还原为 { l1: { l2: [l3...] } } 三层结构
    // 落库只存最细一级，因此需按 code 逐级反查其父级
    const parseReviewReasons = (messageList: string[] | undefined): ReasonSelection => {
        if (!messageList || messageList.length === 0) return {};
        const result: ReasonSelection = {};
        const ensureL1 = (l1Code: string) => {
            if (!result[l1Code]) result[l1Code] = {};
            return result[l1Code];
        };
        const ensureL2 = (l1Code: string, l2Code: string) => {
            const l2Map = ensureL1(l1Code);
            if (!l2Map[l2Code]) l2Map[l2Code] = [];
            return l2Map[l2Code];
        };
        for (const code of messageList) {
            if (!code || code === 'MANUAL_APPROVED' || code === 'OTHER') continue;
            // L1 代码本身（只选L1未选L2时）
            if (L1_L2_REASONS.some(({l1Code}) => l1Code === code)) {
                ensureL1(code);
                continue;
            }
            // L3 代码：反查其 L2 与 L1
            const parentL2 = L3_TO_L2_MAP[code];
            if (parentL2) {
                const parentL1 = L2_TO_L1_MAP[parentL2];
                if (parentL1) {
                    const l3List = ensureL2(parentL1, parentL2);
                    if (!l3List.includes(code)) l3List.push(code);
                }
                continue;
            }
            // 新 L2 枚举代码
            const l1Code = L2_TO_L1_MAP[code];
            if (l1Code) {
                ensureL2(l1Code, code);
                continue;
            }
            // 历史老代码映射
            const legacy = LEGACY_TO_L2_MAP[code];
            if (legacy) {
                ensureL2(legacy.l1Code, legacy.l2Code);
            }
        }
        return result;
    };

    // 改判态下的「取消」：退回「已保存的审核信息」卡片，不关闭抽屉
    const handleCancelSavedEdit = () => {
        setReasonSelection(originalReasonSelection);
        setEditingSavedReview(false);
        setReasonModalVisible(false);
        // 同 closeReasonModal：放弃改判时备注也要回滚，避免残留未提交的输入
        setReviewNote(currentOrder?.podReviewNote ?? '');
        if (currentOrder) {
            setTempSelectedStatus(
                currentOrder.podReviewStatus === POD_REVIEW_STATUS.MANUAL_APPROVED ? true :
                    currentOrder.podReviewStatus === POD_REVIEW_STATUS.MANUAL_DISAPPROVED ? false :
                        currentOrder.podReviewStatus === POD_REVIEW_STATUS.MANUAL_CORRECTED ? true : null
            );
        }
    };

    // 改判态下点「不批准」：浮层内选原因，底部确认提交
    const handleEditDisapproveClick = () => {
        const isStatusSwitch = tempSelectedStatus !== false;
        setTempSelectedStatus(false);
        // 从「批准」切到「不批准」时回填原有原因，作为改判的起点
        if (currentOrder && isStatusSwitch) {
            setReasonSelection(parseReviewReasons(currentOrder.podReviewMessage));
        }
        setFocusedL1(L1_L2_REASONS[0].l1Code);
        setFocusedL2(null);
        setReasonModalVisible(true);
    };

    if (!currentOrder) return null;

    const isApproved = currentOrder.podReviewStatus === POD_REVIEW_STATUS.MANUAL_APPROVED;
    const isDisapproved = currentOrder.podReviewStatus === POD_REVIEW_STATUS.MANUAL_DISAPPROVED;

    // 改判流程（编辑模式 tab 进入，或对已有结论点「修改审核结果」）：
    // 提交将覆盖原审核结果，浮层内文案与按钮相应区分
    const isReasonEditingFlow = isEditMode || editingSavedReview;

    // 「通过」形态的浮层（首次审核与改判共用）：无原因可选，仅填备注并确认
    const isApproveConfirmFlow = tempSelectedStatus === true && reasonModalVisible;

    // 勾选/取消一级：勾选时保留空对象（表示只选到一级），取消时整支移除
    // 勾选同时把焦点切到该级，使右侧栏展开其下级，与点击整行的聚焦行为一致
    const toggleL1 = (l1Code: string) => {
        const willCheck = !(l1Code in reasonSelection);
        setReasonSelection(prev => {
            if (l1Code in prev) {
                const {[l1Code]: _removed, ...rest} = prev;
                return rest;
            }
            return {...prev, [l1Code]: {}};
        });
        if (willCheck) {
            setFocusedL1(l1Code);
            setFocusedL2(null);
        }
    };

    // 勾选/取消二级：取消时连带清掉其下三级
    const toggleL2 = (l1Code: string, l2Code: string) => {
        const willCheck = !(l2Code in (reasonSelection[l1Code] ?? {}));
        setReasonSelection(prev => {
            const l2Map = {...(prev[l1Code] ?? {})};
            if (l2Code in l2Map) {
                delete l2Map[l2Code];
            } else {
                l2Map[l2Code] = [];
            }
            return {...prev, [l1Code]: l2Map};
        });
        if (willCheck) {
            setFocusedL1(l1Code);
            setFocusedL2(l2Code);
        }
    };

    // 勾选/取消三级（同一二级下可多选）
    const toggleL3 = (l1Code: string, l2Code: string, l3Code: string) => {
        setReasonSelection(prev => {
            const l2Map = {...(prev[l1Code] ?? {})};
            const l3List = [...(l2Map[l2Code] ?? [])];
            const idx = l3List.indexOf(l3Code);
            if (idx >= 0) {
                l3List.splice(idx, 1);
            } else {
                l3List.push(l3Code);
            }
            l2Map[l2Code] = l3List;
            return {...prev, [l1Code]: l2Map};
        });
    };

    // 从已选 chips 中移除一项
    const removeChip = (chip: ReasonChip) => {
        if (chip.l3Code && chip.l2Code) {
            toggleL3(chip.l1Code, chip.l2Code, chip.l3Code);
        } else if (chip.l2Code) {
            toggleL2(chip.l1Code, chip.l2Code);
        } else {
            toggleL1(chip.l1Code);
        }
    };

    const moveToNext = () => {
        if (isBatchMode) {
            const nextIndex = internalCurrentIndex + 1;
            if (nextIndex < reviewIds.length) {
                setInternalCurrentIndex(nextIndex);
            } else {
                // 没有下一个订单，关闭详情页
                onClose();
            }
        } else {
            // 非批量模式，或者批量模式但只有一条记录，直接关闭弹框
            onClose();
        }
    };

    // 点「批准」：统一打开浮层填备注并确认。
    // 浮层是「提交审核结论」的唯一载体（批准/不批准/改判都在其中完成），
    // 避免备注、原因、确认散落在页面多处
    const onApprove = () => {
        if (isProcessing || loading) {
            return;
        }
        setTempSelectedStatus(true);
        setReasonSelection({});
        setReasonModalVisible(true);
    };

    // 返回值表示是否已成功提交，供调用方决定要不要关闭浮层
    const doApprove = async (): Promise<boolean> => {
        setIsProcessing(true);
        try {
            const reasons: string[] = [];

            const success = await handleReview(currentOrder.orderId, true, reasons, currentOrder.id, reviewNote);
            if (success) {
                // 审核成功后，立即更新currentOrder状态，将podReviewMessage设置为空数组
                // 这样下次打开详情时，useEffect会根据podReviewStatus判断，不会重新设置不合格原因
                if (currentOrder) {
                    setCurrentOrder({
                        ...currentOrder,
                        podReviewStatus: POD_REVIEW_STATUS.MANUAL_APPROVED,
                        podReviewMessage: []
                    });
                }
                setReasonSelection({});
                setOriginalReasonSelection({});
                setTempSelectedStatus(true);
                moveToNext();
            }
            return success;
        } finally {
            setIsProcessing(false);
        }
    };

    const onDisapprove = async (): Promise<boolean> => {
        if (isProcessing || loading) {
            return false;
        }

        // 与浮层「已选原因路径」同一判据：getAllSelectedCodes 为空即未选任何原因
        if (getAllSelectedCodes(reasonSelection).length === 0) {
            showErrorOnce(t('errors.business.podReview.reasonsRequired'));
            return false;
        }

        setIsProcessing(true);
        try {
            const finalReasons = getAllSelectedCodes(reasonSelection);
            const success = await handleReview(currentOrder.orderId, false, finalReasons, currentOrder.id, reviewNote);
            if (success) {
                // 不通过：立即回写当前订单状态与不合格原因，避免页面看起来未生效
                if (currentOrder) {
                    setCurrentOrder({
                        ...currentOrder,
                        podReviewStatus: POD_REVIEW_STATUS.MANUAL_DISAPPROVED,
                        podReviewMessage: finalReasons,
                    });
                }
                setReasonSelection({});
                moveToNext();
            }
            return success;
        } finally {
            setIsProcessing(false);
        }
    };

    const onSkip = () => {
        moveToNext();
    };

    /**
     * 改判提交（覆盖原审核结果）。
     * 结论与原因由调用方显式传入，避免依赖刚 setState、尚未生效的状态值。
     */
    const onApplyChange = async (approveOverride?: boolean, reasonsOverride?: string[]): Promise<boolean> => {
        // 防止重复点击
        if (isProcessing || loading) {
            return false;
        }

        const nextStatus = approveOverride ?? tempSelectedStatus;
        if (!currentOrder || nextStatus === null || nextStatus === undefined) {
            showErrorOnce(t('errors.business.podReview.selectApproveOrDisapprove'));
            return false;
        }

        const nextReasons = reasonsOverride ?? (nextStatus ? [] : getAllSelectedCodes(reasonSelection));

        if (!nextStatus && nextReasons.length === 0) {
            showErrorOnce(t('errors.business.podReview.reasonsRequired'));
            return false;
        }

        setIsProcessing(true);
        try {
            const originalStatus = currentOrder.podReviewStatus === POD_REVIEW_STATUS.MANUAL_APPROVED ||
                currentOrder.podReviewStatus === POD_REVIEW_STATUS.MANUAL_CORRECTED;

            let hasChange = false;
            if (nextStatus !== originalStatus) {
                hasChange = true;
            } else if (nextStatus === false) {
                hasChange = hasSubstantialReasonChange(reasonSelection, originalReasonSelection);
            } else {
                hasChange = getAllSelectedCodes(originalReasonSelection).length > 0;
            }

            // 无实质变更：仅提示，保持浮层打开让审核员继续调整（沿用旧版的点击后提示方式）
            if (!hasChange && reviewNote === (currentOrder.podReviewNote ?? '')) {
                message.info(t('podReview.messages.noChangesDetected'));
                return false;
            }

            const finalReasons = nextReasons;
            const tempSelectedStatusValue = nextStatus;

            const success = await handleReview(currentOrder.orderId, tempSelectedStatusValue, finalReasons, currentOrder.id, reviewNote);
            if (success) {
                // 审核成功后立即回写currentOrder，避免"批准→不批准"切换看起来未生效
                if (currentOrder) {
                    setCurrentOrder({
                        ...currentOrder,
                        podReviewStatus: tempSelectedStatusValue
                            ? POD_REVIEW_STATUS.MANUAL_APPROVED
                            : POD_REVIEW_STATUS.MANUAL_DISAPPROVED,
                        podReviewMessage: finalReasons,
                    });
                }
                setEditingSavedReview(false);
                setOriginalReasonSelection(reasonSelection);
                message.success(t('podReview.messages.changesAppliedSuccessfully'));
                moveToNext();
            }
            return success;
        } finally {
            setIsProcessing(false);
        }
    };

    const getTitle = () => {
        if (isBatchMode) {
            const current = internalCurrentIndex + 1;
            const total = reviewIds.length;
            return t('podReview.batchReviewTitle', {current, total});
        } else {
            return currentOrder?.trackingNumber || '';
        }
    };

    // 「修改审核结果」：放开顶栏操作按钮，允许对已有结论改判
    const startEditSavedReview = () => {
        const parsed = parseReviewReasons(currentOrder?.podReviewMessage);
        setEditingSavedReview(true);
        // 放开表单：否则原因面板保持禁用，审核员无法调整不合格原因
        // 回填当前结论与原因，使顶栏按钮呈现已选中态，并作为「是否有变更」的比较基准
        setOriginalReasonSelection(parsed);
        setReasonSelection(parsed);
        setTempSelectedStatus(
            currentOrder?.podReviewStatus === POD_REVIEW_STATUS.MANUAL_DISAPPROVED ? false : true
        );
    };

    // 打开不合格原因弹窗：非编辑态下点「不批准」触发，回填已有原因
    const openReasonModal = () => {
        if (isProcessing || loading) return;
        // 浮层形态由 tempSelectedStatus 决定，必须显式置为「不批准」，
        // 否则从已打开的批准浮层点「不批准」时状态停在 true，浮层不切换
        setTempSelectedStatus(false);
        setReasonSelection(parseReviewReasons(currentOrder?.podReviewMessage));
        setFocusedL1(L1_L2_REASONS[0].l1Code);
        setFocusedL2(null);
        setReasonModalVisible(true);
    };

    // 关闭弹窗；编辑/改判态下取消选原因，需回退「不批准」的临时结论，避免顶栏停在未选原因的不批准态
    const closeReasonModal = () => {
        setReasonModalVisible(false);
        // 备注在浮层内编辑，取消时必须回滚到该单已保存的值，
        // 否则未提交的输入会残留（改判场景下还会污染下方「已保存的审核信息」展示）
        setReviewNote(currentOrder?.podReviewNote ?? '');
        if (isReasonEditingFlow) {
            setReasonSelection(originalReasonSelection);
            setTempSelectedStatus(
                currentOrder?.podReviewStatus === POD_REVIEW_STATUS.MANUAL_DISAPPROVED ? false : true
            );
        } else {
            // 首次审核取消：清空临时结论，避免顶栏按钮残留选中态
            setTempSelectedStatus(null);
            setReasonSelection({});
        }
    };

    // 浮层内确认即提交（与原型一致，不再回顶栏做二次确认）：
    // - 新审核：提交不批准
    // - 改判为不批准：覆盖原审核结果
    // - 改判为批准：无原因，直接以「通过」覆盖
    // 仅在提交成功后关闭浮层；校验未通过（原因未选 / 无变更）时保持打开，便于继续调整
    const confirmDisapprove = async () => {
        const submitted = isApproveConfirmFlow
            // 改判走覆盖提交，首次审核走普通通过
            ? await (isReasonEditingFlow ? onApplyChange(true, []) : doApprove())
            : await (isReasonEditingFlow ? onApplyChange() : onDisapprove());
        if (submitted) {
            setReasonModalVisible(false);
        }
    };

    // 三栏联动的原因选择区（一级 / 二级 / 三级 + 已选路径）
    const renderReasonColumns = (chips: ReasonChip[]) => {
        const focusedL1Entry = L1_L2_REASONS.find(r => r.l1Code === focusedL1);
        const col2Codes = focusedL1Entry?.l2Codes ?? [];
        const col3Codes = focusedL2 ? (L3_BY_L2[focusedL2] ?? []) : [];

        const focusL1 = (l1Code: string) => {
            setFocusedL1(l1Code);
            setFocusedL2(null);
        };

        const colHeader = (step: string, title: string, hint: string) => (
            <div className="flex items-center gap-1.5 px-2.5 py-2 border-b border-gray-100">
                <span className="flex-shrink-0 w-[18px] h-[18px] rounded-full bg-ant-purple-6 text-white text-[11px] leading-[18px] text-center">{step}</span>
                <span className="text-[13px] font-semibold text-gray-800">{title}</span>
                <span className="text-[11px] text-gray-400">{hint}</span>
            </div>
        );

        const rowClass = (checked: boolean, focused: boolean) =>
            `flex items-center gap-2 px-2 py-2 rounded-lg my-0.5 cursor-pointer border-l-[3px] ${
                focused ? 'border-ant-purple-8 bg-ant-purple-1'
                    : checked ? 'border-ant-purple-4 bg-ant-purple-1/40'
                        : 'border-transparent hover:bg-gray-50'
            }`;

        const checkboxClass = 'flex-shrink-0 [&_.ant-checkbox-checked_.ant-checkbox-inner]:bg-ant-purple-6 [&_.ant-checkbox-checked_.ant-checkbox-inner]:border-ant-purple-6';

        return (
            <div className="border border-ant-purple-2 rounded-[10px]">
                <div className="grid grid-cols-3 divide-x divide-gray-100">
                    {/* 一级原因 */}
                    <div className="flex flex-col min-w-0">
                        {colHeader('1', t('podReview.detail.reasonLevel1'), t('podReview.detail.reasonRequired'))}
                        <div className="p-1.5 overflow-y-auto" style={{maxHeight: 260, minHeight: 200}}>
                            {L1_L2_REASONS.map(({l1Code}) => {
                                const checked = l1Code in reasonSelection;
                                const count = getAllSelectedCodes(
                                    {[l1Code]: reasonSelection[l1Code] ?? {}} as ReasonSelection
                                ).length;
                                return (
                                    <div
                                        key={l1Code}
                                        className={rowClass(checked, focusedL1 === l1Code)}
                                        onClick={() => focusL1(l1Code)}
                                    >
                                        <Checkbox
                                            checked={checked}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={() => toggleL1(l1Code)}
                                            className={checkboxClass}
                                        />
                                        <span className="text-[13px] truncate flex-1">
                                            {t(`podReview.reasons.${l1Code}`)}
                                        </span>
                                        {checked && count > 0 && (
                                            <span className="flex-shrink-0 text-[11px] font-semibold text-ant-purple-7 bg-ant-purple-2 rounded-[9px] px-1.5">
                                                {count}
                                            </span>
                                        )}
                                        <span className="text-gray-300 text-xs">›</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* 二级原因 */}
                    <div className="flex flex-col min-w-0">
                        {colHeader('2', t('podReview.detail.reasonLevel2'), t('podReview.detail.reasonOptional'))}
                        <div className="p-1.5 overflow-y-auto" style={{maxHeight: 260, minHeight: 200}}>
                            {col2Codes.map(l2Code => {
                                const checked = !!reasonSelection[focusedL1] && l2Code in reasonSelection[focusedL1];
                                const l3Count = reasonSelection[focusedL1]?.[l2Code]?.length ?? 0;
                                const hasL3 = (L3_BY_L2[l2Code] ?? []).length > 0;
                                return (
                                    <div
                                        key={l2Code}
                                        className={rowClass(checked, focusedL2 === l2Code)}
                                        onClick={() => setFocusedL2(l2Code)}
                                    >
                                        <Checkbox
                                            checked={checked}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={() => toggleL2(focusedL1, l2Code)}
                                            className={checkboxClass}
                                        />
                                        <span className="text-[13px] truncate flex-1">
                                            {t(`podReview.reasons.${l2Code}`, {defaultValue: l2Code})}
                                        </span>
                                        {hasL3 && (
                                            <span className="flex-shrink-0 text-[10px] text-ant-purple-7 bg-ant-purple-2 rounded px-1">
                                                {l3Count > 0 ? l3Count : '▾'}
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    {/* 三级原因 */}
                    <div className="flex flex-col min-w-0">
                        {colHeader('3', t('podReview.detail.reasonLevel3'), t('podReview.detail.reasonOptional'))}
                        <div className="p-1.5 overflow-y-auto" style={{maxHeight: 260, minHeight: 200}}>
                            {col3Codes.length === 0 ? (
                                <div className="text-[11.5px] text-gray-400 px-2 py-2">
                                    {focusedL2 ? t('podReview.detail.reasonNoLevel3') : t('podReview.detail.reasonPickLevel2')}
                                </div>
                            ) : col3Codes.map(l3Code => {
                                const checked = !!focusedL2
                                    && (reasonSelection[focusedL1]?.[focusedL2] ?? []).includes(l3Code);
                                return (
                                    <div
                                        key={l3Code}
                                        className={rowClass(checked, false)}
                                        onClick={() => focusedL2 && toggleL3(focusedL1, focusedL2, l3Code)}
                                    >
                                        <Checkbox
                                            checked={checked}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={() => focusedL2 && toggleL3(focusedL1, focusedL2, l3Code)}
                                            className={checkboxClass}
                                        />
                                        <span className="text-[13px] truncate flex-1">
                                            {t(`podReview.reasons.${l3Code}`, {defaultValue: l3Code})}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* 已选原因路径 */}
                <div className="border-t border-gray-100 px-2.5 py-2">
                    <div className="text-[11px] text-gray-400 mb-1.5">{t('podReview.detail.selectedReasonPaths')}</div>
                    {chips.length === 0 ? (
                        <div className="text-[12px] text-gray-300">{t('podReview.detail.noReasonSelected')}</div>
                    ) : (
                        <div className="flex flex-wrap gap-1.5">
                            {chips.map(chip => (
                                <Tag
                                    key={chip.key}
                                    closable
                                    onClose={(e) => {
                                        e.preventDefault();
                                        removeChip(chip);
                                    }}
                                    className="m-0 text-[11.5px] bg-ant-purple-1 border-ant-purple-3 text-ant-purple-9"
                                >
                                    {chip.text}
                                </Tag>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // 收件人地址单行文案（顶部信息条横排展示）
    const recipientText = (() => {
        const a = currentOrder.recipientAddress;
        if (!a) return '—';
        const phone = a.phoneNumber
            ? `${a.countryCode && a.countryCode !== '0' ? `+${a.countryCode} ` : ''}${a.phoneNumber}${a.extension ? ` ext. ${a.extension}` : ''}`
            : '';
        return [
            a.fullName,
            a.company ? `(${a.company})` : '',
            [a.addressLine1, a.addressLine2].filter(Boolean).join(' '),
            [a.city, a.state, a.zipcode].filter(Boolean).join(', '),
            phone,
        ].filter(Boolean).join(' · ');
    })();

    // 信息项：label 定宽左对齐 + value 撑满，label 与 value 各自起始位置在网格中纵向对齐；
    // fullRow 用于长文本独占整行
    // titleText：node 非字符串时补原生 title，避免 truncate 后无法查看全文
    const infoItem = (label: string, node: React.ReactNode, fullRow = false, tip?: string, titleText?: string) => (
        <div className={`flex items-baseline gap-2 min-w-0 ${fullRow ? 'col-span-3' : ''}`}>
            <span className="flex-shrink-0 min-w-[72px] text-gray-400 whitespace-nowrap">
                {label}
                {tip && (
                    <Tooltip title={tip}>
                        <QuestionCircleOutlined className="ml-1 text-xs" />
                    </Tooltip>
                )}
            </span>
            <span className="flex-1 min-w-0 text-gray-800 truncate" title={titleText ?? (typeof node === 'string' ? node : undefined)}>
                {node}
            </span>
        </div>
    );

    // 已有审核结论且未进入改判态：展示「已保存的审核信息」卡片，顶栏收起全部操作按钮。
    // isEditMode（等待司机更新 / 已终止 tab）进入的单据本身就已审核过，同样走此路径，
    // 需点卡片内「修改审核结果」才放开顶栏操作。
    const hasSavedReview = (isApproved || isDisapproved) && !editingSavedReview;

    const reasonChips = buildReasonChips(reasonSelection);

    // 提交按钮禁用原因：未选原因 > 无变更；两者提示不同，便于审核员知道该做什么

    return (
        <>
            <Drawer
                placement="right"
                width="calc(100vw - 80px)"
                onClose={onClose}
                open={visible}
                styles={{body: {padding: 0, background: '#F3F2F7'}}}
                closable={false}
                title={null}
                headerStyle={{display: 'none'}}
            >
            <Skeleton loading={detailLoading} active>
                <div className="relative h-full flex flex-col bg-[#F3F2F7]">
                    {/* 顶栏：返回 + 标题 + 状态 + 操作按钮 */}
                    <div className="flex-shrink-0 h-[52px] bg-white border-b border-gray-200 flex items-center gap-3 px-6">
                        <ArrowLeftOutlined
                            className="text-base text-gray-500 cursor-pointer hover:text-gray-700"
                            onClick={onClose}
                        />
                        <span className="text-[15px] font-semibold">{getTitle()}</span>
                        <span
                            className={`px-2 py-0.5 rounded-[10px] text-[11.5px] border ${
                                isApproved ? 'border-green-200 bg-green-50 text-green-700'
                                    : isDisapproved ? 'border-red-200 bg-red-50 text-red-700'
                                        : 'border-gray-200 bg-gray-50 text-gray-500'
                            }`}
                        >
                            {isApproved ? t('enum.podReviewStatus.MANUAL_APPROVED')
                                : isDisapproved ? t('enum.podReviewStatus.MANUAL_DISAPPROVED')
                                    : t('enum.podReviewStatus.MANUAL_PENDING')}
                        </span>

                        <div className="ml-auto flex items-center gap-2">
                            {/* 已有结论且未点「修改审核结果」时，收起批准/不批准，避免与卡片内操作重复 */}
                            {!hasSavedReview && (
                                <>
                                    <Button
                                        icon={<CheckOutlined/>}
                                        onClick={onApprove}
                                        loading={loading || isProcessing || detailLoading}
                                        className={verdictButtonClass(
                                            true,
                                            tempSelectedStatus !== null ? tempSelectedStatus === true : isApproved
                                        )}
                                    >
                                        {t('podReview.actions.approve')}
                                    </Button>
                                    <Button
                                        icon={<CloseOutlined/>}
                                        onClick={isReasonEditingFlow ? handleEditDisapproveClick : openReasonModal}
                                        loading={loading || isProcessing || detailLoading}
                                        className={verdictButtonClass(
                                            false,
                                            tempSelectedStatus !== null ? tempSelectedStatus === false : isDisapproved
                                        )}
                                    >
                                        {t('podReview.actions.disapprove')}
                                    </Button>
                                    {/* 改判态下提供退出口，回到「已保存的审核信息」展示态 */}
                                    {editingSavedReview && (
                                        <Button onClick={handleCancelSavedEdit}>
                                            {t('podReview.actions.cancel')}
                                        </Button>
                                    )}
                                </>
                            )}
                            {/* 已有结论：入口同样收在顶栏，页面中部卡片仅作展示，避免操作分散在多处 */}
                            {hasSavedReview && (
                                <>
                                    <Button onClick={startEditSavedReview}>
                                        {t('podReview.actions.modifyReviewResult')}
                                    </Button>
                                    <Button
                                        type="primary"
                                        className="bg-ant-purple-6 hover:bg-ant-purple-7 border-ant-purple-6 hover:border-ant-purple-7"
                                        onClick={isBatchMode ? moveToNext : onClose}
                                    >
                                        {isBatchMode && internalCurrentIndex + 1 < reviewIds.length
                                            ? `${t('podReview.actions.next')} →`
                                            : t('podReview.actions.backToList')}
                                    </Button>
                                </>
                            )}
                            {!hasSavedReview && isBatchMode && !showNoMore && (
                                <Button
                                    onClick={onSkip}
                                    loading={loading || isProcessing || detailLoading}
                                    className="text-gray-600 hover:!border-ant-purple-4"
                                >
                                    {t('podReview.actions.skip')} →
                                </Button>
                            )}
                            {isBatchMode && showNoMore && (
                                <span className="text-gray-400 text-sm">{t('podReview.messages.noMore')}</span>
                            )}
                        </div>
                    </div>

                    <div className="flex-1 min-h-0 flex flex-col gap-2.5 px-6 py-3">
                        {/* 横排信息条 */}
                        {/* 三列固定网格：短字段每行三项对齐，长文本（地址类）整行独占，避免条件项导致错位 */}
                        <div className="flex-shrink-0 bg-white border border-gray-200 rounded-[10px] px-5 py-3 grid grid-cols-3 gap-x-8 gap-y-2 text-[14px] leading-6">
                            {infoItem(t('podReview.detail.trackingNumber'), (
                                <span
                                    className="font-semibold text-ant-purple-6 cursor-pointer hover:underline"
                                    onClick={() => setParcelDetailVisible(true)}
                                >
                                    {currentOrder.trackingNumber}
                                </span>
                            ))}
                            {infoItem(t('podReview.detail.signedAt'), currentOrder.firstPodUploadTime
                                ? new Date(currentOrder.firstPodUploadTime).toLocaleString()
                                : '—')}
                            {infoItem(t('podReview.table.driver'), currentOrder.driver?.name || '—')}

                            {infoItem(t('podReview.detail.deliveredLocation'), currentOrder.deliveredLocation
                                ? (currentOrder.deliveredLocation.latitude === 0 && currentOrder.deliveredLocation.longitude === 0
                                    ? '0,0'
                                    : `${currentOrder.deliveredLocation.latitude.toFixed(7)}, ${currentOrder.deliveredLocation.longitude.toFixed(7)}`)
                                : '—')}
                            {infoItem(t('podReview.detail.deliveryDistanceLabel'), deliveryDistance != null
                                ? `${deliveryDistance.toFixed(2)} ${t('podReview.detail.deliveryDistanceUnit')}`
                                : '—')}
                            {infoItem(t('podReview.detail.lastReviewer'), currentOrder.lastReviewer?.name || '—')}

                            {/* 地址与评论较长，各占整行 */}
                            {infoItem(t('podReview.detail.recipient'), recipientText, true)}
                            {currentOrder.bFullAddress && infoItem(
                                t('podReview.detail.abAddress'),
                                <span className="bg-gray-100 rounded px-2 py-0.5">{currentOrder.bFullAddress}</span>,
                                true,
                                t('podReview.detail.abAddressTooltip'),
                                currentOrder.bFullAddress
                            )}
                            {currentOrder.podExemptHit && infoItem(
                                t('podReview.detail.podExempt'),
                                <span className="inline-flex items-center gap-1 flex-wrap">
                                    <Tag color="green" className="m-0">
                                        {currentOrder.podExemptTargetType === 'FENCE'
                                            ? t('podReview.detail.podExemptFence')
                                            : t('podReview.detail.podExemptAddress')}
                                    </Tag>
                                    {podExemptReasonName && <span className="text-gray-700">{podExemptReasonName}</span>}
                                </span>,
                                true,
                                t('podReview.detail.podExemptTooltip'),
                                podExemptReasonName ?? undefined
                            )}
                            {infoItem(t('podReview.detail.reviewComment'), formatReviewMessage(currentOrder.podReviewMessage) || '—', true)}
                        </div>

                        {/* 已保存的审核信息（已审核且非编辑态时展示） */}
                        {hasSavedReview && (
                            <div className="flex-shrink-0 bg-white border border-ant-purple-2 rounded-[10px]">
                                <div className="px-4 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-2 text-[12.5px]">
                                    <span className="font-semibold text-[13.5px]">{t('podReview.detail.savedReviewInfo')}</span>
                                    <span className={`px-2 py-0.5 rounded-[10px] text-[11.5px] border ${
                                        isApproved ? 'border-green-200 bg-green-50 text-green-700' : 'border-red-200 bg-red-50 text-red-700'
                                    }`}>
                                        {isApproved ? t('enum.podReviewStatus.MANUAL_APPROVED') : t('enum.podReviewStatus.MANUAL_DISAPPROVED')}
                                    </span>
                                    <span className="text-gray-400">
                                        {t('podReview.detail.lastReviewer')}{' '}
                                        <span className="text-gray-800">{currentOrder.lastReviewer?.name || '—'}</span>
                                    </span>
                                    <span className="text-gray-400">
                                        {t('podReview.table.reviewTime')}{' '}
                                        <span className="text-gray-800">
                                            {currentOrder.reviewTime ? new Date(currentOrder.reviewTime).toLocaleString() : '—'}
                                        </span>
                                    </span>
                                    <span className="text-gray-400">
                                        {t('podReview.detail.reviewNote')}{' '}
                                        <span className="text-gray-800">{reviewNote || t('podReview.detail.noReviewNote')}</span>
                                    </span>
                                </div>

                                {isDisapproved && reasonChips.length > 0 && (
                                    <div className="border-t border-gray-100 px-4 py-2 flex flex-wrap items-center gap-2 text-[12.5px]">
                                        <span className="text-gray-400">{t('podReview.detail.disapprovalReason')}</span>
                                        {reasonChips.map(chip => (
                                            <Tag key={chip.key} className="m-0 text-[11.5px] bg-ant-purple-1 border-ant-purple-3 text-ant-purple-9">
                                                {chip.text}
                                            </Tag>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* POD 图片展示区域：完全沿用改版前的实现（grid 等宽 + aspect-[9/16] + 75vh + object-cover），
                            仅保留其原有的 ≤3 张 / >3 张两套分支，不做任何尺寸策略调整 */}
                        <div className="flex-1 min-h-0">
                            <div className={`grid gap-4 ${podImages.length <= 3 ? 'grid-cols-3 h-[75vh]' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 max-h-[75vh] overflow-y-auto'}`}>
                                {podImages.length > 0 ? (
                                    podImages.map((img, index) => (
                                        <div
                                            key={img.s3Key}
                                            className={`relative aspect-[9/16] border rounded-lg overflow-hidden transition-colors bg-white ${img.isRisky ? 'border-red-500' : 'border-gray-300 hover:border-blue-400'} ${podImages.length <= 3 ? 'h-full' : 'max-h-[75vh] mx-auto'}`}
                                        >
                                            {img.isRisky && (
                                                <div
                                                    className="absolute top-0 left-0 right-0 z-10 bg-red-500/90 text-white text-xs font-medium text-center py-1 pointer-events-none">
                                                    {t('podReview.detail.riskyImageHint')}
                                                </div>
                                            )}
                                            <Image
                                                src={img.url}
                                                alt={`POD ${index + 1}`}
                                                className="object-cover w-full h-full"
                                                preview={{
                                                    mask: (
                                                        <div className="flex items-center justify-center">
                                                            <span className="text-white text-sm font-medium">
                                                                {t('podReview.detail.clickToPreview')}
                                                            </span>
                                                        </div>
                                                    )
                                                }}
                                            />
                                        </div>
                                    ))
                                ) : (
                                    [0, 1, 2].map(i => (
                                        <div
                                            key={i}
                                            className="aspect-[9/16] border border-gray-300 rounded-lg flex items-center justify-center bg-white h-full"
                                        >
                                            <CameraOutlined className="text-8xl text-gray-400"/>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                    {/* 不合格原因选择面板：紧贴顶栏下方展开，视觉上由「批准 / 不批准」按钮延伸而来。
                        无遮罩，浮于内容之上（与原型一致），仍可对照下方照片 */}
                    {reasonModalVisible && (
                        <div className="absolute top-[60px] right-[22px] z-[80] max-h-[calc(100%-76px)] flex items-start">
                            <div
                                className="relative flex flex-col bg-white border border-ant-purple-2 rounded-xl overflow-hidden"
                                style={{
                                    // 「通过」仅有备注一项，收窄为确认框体量（需容纳标题 + 覆盖提示标签）；
                                    // 选原因需三栏并排，保持宽版
                                    width: isApproveConfirmFlow ? 480 : 'min(680px, 56vw)',
                                    minWidth: isApproveConfirmFlow ? undefined : 600,
                                    // 上限继承外层 max-h（相对抽屉内容区而非视口），超出部分由内容区滚动
                                    maxHeight: '100%',
                                    boxShadow: '0 20px 54px rgba(31,27,45,.26)',
                                }}
                            >
                                <div className="flex-shrink-0 px-5 py-3.5 border-b border-gray-100 flex items-center gap-2.5">
                                    <span className={`text-[15px] font-bold ${isApproveConfirmFlow ? 'min-w-0' : 'flex-shrink-0'}`}>
                                        {isApproveConfirmFlow
                                            ? (isReasonEditingFlow
                                                ? t('podReview.messages.approveModifyTitle')
                                                : t('podReview.messages.approveTitle'))
                                            : (isReasonEditingFlow
                                                ? t('podReview.messages.disapproveModifyTitle')
                                                : t('podReview.detail.selectReasonTitle'))}
                                    </span>
                                    {!isApproveConfirmFlow && (
                                        <span className="text-[11.5px] text-gray-500 min-w-0">
                                            {t('podReview.detail.selectReasonHint')}
                                        </span>
                                    )}
                                    {isReasonEditingFlow && (
                                        <span className="flex-shrink-0 whitespace-nowrap text-[11.5px] text-orange-600 bg-orange-50 border border-orange-200 rounded-md px-2 py-1">
                                            {t('podReview.detail.editingOverwriteHint')}
                                        </span>
                                    )}
                                    {!isApproveConfirmFlow && (
                                        <span className="ml-auto text-[11.5px] text-ant-purple-7 bg-ant-purple-1 rounded-[10px] px-2 py-0.5 flex-shrink-0">
                                            {t('podReview.detail.selectedCount', {count: reasonChips.length})}
                                        </span>
                                    )}
                                    <CloseOutlined
                                        className={`flex-shrink-0 text-gray-400 hover:text-gray-600 cursor-pointer ${isApproveConfirmFlow ? 'ml-auto' : ''}`}
                                        onClick={closeReasonModal}
                                    />
                                </div>

                                <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
                                    {/* 操作影响说明：通过无原因可选，仅说明结果；改判额外说明会覆盖原记录 */}
                                    {(isApproveConfirmFlow || isReasonEditingFlow) && (
                                        <div className={`text-[13px] text-gray-600 leading-6 ${isApproveConfirmFlow ? '' : 'mb-3'}`}>
                                            {isApproveConfirmFlow
                                                ? (isReasonEditingFlow
                                                    ? t('podReview.messages.approveModifyContent')
                                                    : t('podReview.messages.approveContent'))
                                                : t('podReview.messages.disapproveModifyContent')}
                                        </div>
                                    )}

                                    {!isApproveConfirmFlow && renderReasonColumns(reasonChips)}

                                    {/* 历史「其他」自由文本：仅只读展示，不参与本次提交 */}
                                    {!isApproveConfirmFlow && legacyOtherText && (
                                        <div className="mt-3">
                                            <div className="text-[11px] text-gray-400 mb-1">{t('podReview.reasons.OTHER')}</div>
                                            <Input value={legacyOtherText} disabled/>
                                        </div>
                                    )}

                                    {/* 备注：与原因、确认同处一层，构成完整的「审核结论」输入单元。
                                        底部留白给 showCount 字数统计，避免被滚动容器裁切 */}
                                    <div className="mt-3 pb-5">
                                        <div className="text-[11px] text-gray-400 mb-1">{t('podReview.detail.reviewNote')}</div>
                                        <Input.TextArea
                                            value={reviewNote}
                                            placeholder={t('podReview.detail.reviewNotePlaceholder')}
                                            maxLength={120}
                                            showCount
                                            rows={2}
                                            onChange={(e) => setReviewNote(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="flex-shrink-0 px-4 py-3 border-t border-gray-100 flex items-center gap-2">
                                    <Button className="ml-auto" onClick={closeReasonModal}>
                                        {t('podReview.actions.cancel')}
                                    </Button>
                                    {/* 校验沿用旧版：点击后再提示（原因必填 / 未检测到更改），不预先禁用按钮。
                                        提交按钮统一用主色紫；绿/红在本页专用于表达审核状态，不挪作按钮主色，
                                        仅「首次不批准」保留危险色以强调其破坏性 */}
                                    <Button
                                        danger={!isApproveConfirmFlow && !isReasonEditingFlow}
                                        type="primary"
                                        loading={loading || isProcessing}
                                        onClick={confirmDisapprove}
                                        className={isApproveConfirmFlow || isReasonEditingFlow
                                            ? 'bg-ant-purple-6 hover:!bg-ant-purple-7 border-ant-purple-6 hover:!border-ant-purple-7'
                                            : ''}
                                    >
                                        {isApproveConfirmFlow
                                            ? t('components.button.confirm')
                                            : isReasonEditingFlow
                                                ? t('podReview.actions.saveChanges')
                                                : t('podReview.actions.disapprove')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
            </Skeleton>
            </Drawer>

            {/* 包裹详情抽屉 */}
            {currentOrder && (
                <CentralParcelDetailDrawer
                    visible={parcelDetailVisible}
                    onClose={() => setParcelDetailVisible(false)}
                    orderId={currentOrder.orderId.toString()}
                    userRole={getUserInfo()?.account?.role?.name || ''}
                    hideEditActions={true}
                    onRefreshList={() => {
                        // 刷新列表的回调，可以根据需要添加刷新逻辑
                    }}
                />
            )}
        </>
    );
};

export default PodReviewDetailDrawer;
